import { Editor } from "react-draft-wysiwyg"
import { EditorState, ContentState, convertToRaw } from "draft-js"
import draftToHtml from "draftjs-to-html"
import htmlToDraft from "html-to-draftjs"
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import "../style/blockEditorHtmlEditor.scss"
import { Button } from "@mui/material"
import { handlePastedText } from "../../../services/utilities/htmlEditorUtils"

const BlockEditorHtmlEditor = ({
  text,
  setText,
  max,
  min,
  updated,
  error,
  setError,
  errorType,
  setErrorType,
  disabled = false,
}: {
  text: string
  setText: Dispatch<SetStateAction<string>>
  max: number
  min: number
  updated: boolean
  error?: boolean
  setError?: Dispatch<SetStateAction<boolean>>
  errorType?: string
  setErrorType?: Dispatch<SetStateAction<string>>
  disabled?: boolean
}) => {
  // placeholder
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(true)

  // has focus
  const [hasFocus, setHasFocus] = useState<boolean>(false)

  // editor state
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createWithContent(
      ContentState.createFromBlockArray(
        htmlToDraft(text ?? "").contentBlocks,
        htmlToDraft(text ?? "").entityMap
      )
    )
  )

  // update editor state when text prop changes
  useEffect(() => {
    if (
      text !==
      draftToHtml(convertToRaw(editorState.getCurrentContent()))
        .replaceAll("\n", "")
        .replaceAll("&nbsp;", "")
    ) {
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromBlockArray(
            htmlToDraft(text ?? "").contentBlocks,
            htmlToDraft(text ?? "").entityMap
          )
        )
      )
    }
  }, [updated])

  // set text when editor state changes
  useEffect(() => {
    let currentContent = draftToHtml(
      convertToRaw(editorState.getCurrentContent())
    )
      .replaceAll("\n", "")
      .replaceAll("&nbsp;", "")

    setText(currentContent)

    if (currentContent.replace(/(<([^>]+)>)/gi, "").length) {
      setShowPlaceholder(true)
    }

    if (setError && setErrorType) {
      if (currentContent.replace(/(<([^>]+)>)/gi, "").length > max) {
        setError(true)
        setErrorType("Maximum length is " + max + " characters")
      } else if (currentContent.replace(/(<([^>]+)>)/gi, "").length <= min) {
        setError(true)
        setErrorType("This field is required")
      } else {
        setError(false)
        setErrorType("")
      }
    }
  }, [editorState])

  // show html
  const [showHtml, setShowHtml] = useState<boolean>(false)

  return (
    <div
      className="notranslate"
      style={{ position: "relative", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          position: "absolute",
          top: showPlaceholder ? -10 : 49,
          left: 9,
          color: error
            ? "#d3302f"
            : disabled
            ? "#9e9e9f"
            : hasFocus
            ? "#000000"
            : "#939EAA",
          fontSize: showPlaceholder ? 12 : "1rem",
          backgroundColor: "white",
          paddingLeft: 5,
          paddingRight: 5,
          height: 12,
          transition: "180ms",
        }}
      >
        Text
      </div>
      <Button
        style={{ position: "absolute", top: 12, right: 11, height: 32 }}
        variant="outlined"
        onClick={() => {
          setShowHtml(!showHtml)
        }}
      >
        {showHtml ? "Show Editor" : "Show HTML"}
      </Button>
      {showHtml ? (
        <div
          style={{
            height: "auto",
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 59,
            paddingBottom: 16,
            cursor: "default",
          }}
          className={
            disabled
              ? "wrapper-disabled"
              : error && hasFocus
              ? "wrapper-error-focus-prismic"
              : error
              ? "wrapper-error-prismic"
              : hasFocus
              ? "wrapper-focus"
              : "wrapper"
          }
        >
          {draftToHtml(convertToRaw(editorState.getCurrentContent()))}
        </div>
      ) : (
        <Editor
          readOnly={disabled}
          onFocus={() => {
            setShowPlaceholder(true)
            setHasFocus(true)
          }}
          onBlur={() => {
            setHasFocus(false)

            if (
              !draftToHtml(convertToRaw(editorState.getCurrentContent()))
                .replaceAll("\n", "")
                .replace(/(<([^>]+)>)/gi, "").length
            ) {
              setShowPlaceholder(false)
            }
          }}
          editorClassName={disabled ? "editor-disabled" : "editor"}
          toolbarClassName={disabled ? "toolbar-disabled" : "toolbar"}
          wrapperClassName={
            disabled
              ? "wrapper-disabled"
              : error && hasFocus
              ? "wrapper-error-focus-prismic"
              : error
              ? "wrapper-error-prismic"
              : hasFocus
              ? "wrapper-focus"
              : "wrapper"
          }
          editorState={editorState}
          onEditorStateChange={setEditorState}
          toolbar={{
            options: ["inline", "link", "list", "history"],
            inline: {
              options: [
                "bold",
                "italic",
                "underline",
                "superscript",
                "subscript",
              ],
            },
            list: {
              options: ["unordered"],
            },
          }}
          // stripPastedStyles
          handlePastedText={(_, html, editorState) =>
            handlePastedText(html, editorState, setEditorState)
          }
          // handleKeyCommand={(command) => {
          //   if (command === "split-block") {
          //     const newEditorState = RichUtils.insertSoftNewline(editorState)
          //     setEditorState(newEditorState)

          //     return "handled"
          //   }

          //   return "not-handled"
          // }}
        />
      )}
      {error && (
        <p
          style={{
            color: "#d3302f",
            fontSize: "0.75rem",
            lineHeight: 1.66,
            marginLeft: 14,
            marginTop: 4,
          }}
        >
          {errorType}
        </p>
      )}
      <div
        style={{
          position: "absolute",
          bottom: error ? 24 : 0,
          right: 5,
          color: error
            ? "#d3302f"
            : disabled
            ? "#9e9e9f"
            : hasFocus
            ? "#000000"
            : "rgba(0, 0, 0, 0.6)",
          fontSize: 12,
        }}
      >
        {
          draftToHtml(convertToRaw(editorState.getCurrentContent()))
            .replaceAll("\n", "")
            .replaceAll("&nbsp;", "")
            .replace(/(<([^>]+)>)/gi, "").length
        }
      </div>
    </div>
  )
}

export default BlockEditorHtmlEditor
